import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Index from "./pages/Index";
import PropertiesCatalogPage from "./pages/PropertiesCatalogPage";
import PricingPage from "./pages/PricingPage";
import AboutPage from "./pages/AboutPage";
import CRMPage from "./pages/services/CRMPage";
import ValuationPage from "./pages/services/ValuationPage";
import LegalAccountingPage from "./pages/services/LegalAccountingPage";
import DigitalEcosystemPage from "./pages/services/DigitalEcosystemPage";
import SellDirectPage from "./pages/services/SellDirectPage";
import ComparisonPage from "./pages/ComparisonPage";
import { PropertyDetailPage } from "./pages/PropertyDetailPage";
import PropertyManagement from "./pages/PropertyManagement";
import { useAuth } from './hooks/useAuth';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { PropertiesPage } from './pages/dashboard/PropertiesPage';
import { FavoritesPage } from './pages/dashboard/FavoritesPage';
import { MessagesPage } from './pages/dashboard/MessagesPage';
import { NotificationsPage } from './pages/dashboard/NotificationsPage';
import { HistoryPage } from './pages/dashboard/HistoryPage';
import { SettingsPage } from './pages/dashboard/SettingsPage';
import { CRMClientsPage } from './pages/dashboard/CRMClientsPage';
import { CRMVisitsPage } from './pages/dashboard/CRMVisitsPage';
import { CRMPipelinePage } from './pages/dashboard/CRMPipelinePage';
import { CRMReportsPage } from './pages/dashboard/CRMReportsPage';
import { ValuationsPage } from './pages/dashboard/ValuationsPage';
import { RealEstateMapPage } from './pages/dashboard/RealEstateMapPage';
import { SupportTicketsPage } from './pages/dashboard/SupportTicketsPage';
import { SubscriptionsPage } from './pages/dashboard/SubscriptionsPage';
import { BulkUploadPage } from './pages/dashboard/BulkUploadPage';
import { Navbar } from './components/Navbar';
import { getClientEnv, injectEnvToWindow } from './config/clientEnv';
import { ComparisonBar } from './components/ComparisonBar';

const queryClient = new QueryClient();

// Obtener variables de entorno del cliente solo cuando se ejecuta en el navegador
const clientEnv = typeof window !== 'undefined' ? getClientEnv() : { googleMapsApiKey: '' };

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

const App = () => {
  const { loadUser } = useAuth();

  useEffect(() => {
    console.log("App: Cargando configuración y usuario...");
    
    // Inyectar variables de entorno al objeto window
    injectEnvToWindow();
    
    // Cargar el usuario si hay un token
    loadUser().then(() => {
      console.log("App: Usuario cargado correctamente");
    }).catch(error => {
      console.error("App: Error al cargar el usuario:", error);
    });
  }, [loadUser]);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <Router>
          <ComparisonBar />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/propiedades" element={<PropertiesCatalogPage />} />
            <Route path="/planes" element={<PricingPage />} />
            <Route path="/nosotros" element={<AboutPage />} />
            <Route path="/servicios/crm" element={<CRMPage />} />
            <Route path="/servicios/valorizacion" element={<ValuationPage />} />
            <Route path="/servicios/legal-contable" element={<LegalAccountingPage />} />
            <Route path="/servicios/ecosistema-digital" element={<DigitalEcosystemPage />} />
            <Route path="/servicios/vender-directo" element={<SellDirectPage />} />
            <Route path="/comparar" element={<ComparisonPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/property/:id" element={<PropertyDetailPage />} />
            <Route
              path="/property/manage"
              element={
                <ProtectedRoute>
                  <PropertyManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/property-management/:id"
              element={
                <ProtectedRoute>
                  <PropertyManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/publicar"
              element={
                <ProtectedRoute>
                  <PropertyManagement />
                </ProtectedRoute>
              }
            />
            {/* Dashboard Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/properties"
              element={
                <ProtectedRoute>
                  <PropertiesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/properties"
              element={
                <ProtectedRoute>
                  <PropertiesPage adminMode={true} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin/bulk-upload"
              element={
                <ProtectedRoute>
                  <BulkUploadPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/properties/:id/edit"
              element={
                <ProtectedRoute>
                  <PropertyManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/favorites"
              element={
                <ProtectedRoute>
                  <FavoritesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/messages"
              element={
                <ProtectedRoute>
                  <MessagesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/notifications"
              element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/history"
              element={
                <ProtectedRoute>
                  <HistoryPage />
                </ProtectedRoute>
              }
            />
            {/* Nuevas Rutas CRM */}
            <Route
              path="/dashboard/crm/clients"
              element={
                <ProtectedRoute>
                  <CRMClientsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/crm/visits"
              element={
                <ProtectedRoute>
                  <CRMVisitsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/crm/pipeline"
              element={
                <ProtectedRoute>
                  <CRMPipelinePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/crm/reports"
              element={
                <ProtectedRoute>
                  <CRMReportsPage />
                </ProtectedRoute>
              }
            />
            {/* Nuevas Rutas Herramientas */}
            <Route
              path="/dashboard/valuations"
              element={
                <ProtectedRoute>
                  <ValuationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/map"
              element={
                <ProtectedRoute>
                  <RealEstateMapPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/support"
              element={
                <ProtectedRoute>
                  <SupportTicketsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/subscriptions"
              element={
                <ProtectedRoute>
                  <SubscriptionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/profile"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;